import { useState } from 'react';
import { submitContactForm } from '../utils/api';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { Mail, Phone, Clock, MessageSquare, CheckCircle2, MapPin, Globe } from 'lucide-react';

// Country list
const countries = [
  "Afghanistan", "Albania", "Algeria", "Angola", "Argentina", "Armenia", "Australia", "Austria", "Azerbaijan",
  "Bahrain", "Bangladesh", "Belarus", "Belgium", "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei", "Bulgaria",
  "Cambodia", "Cameroon", "Canada", "Chad", "Chile", "China", "Colombia", "Congo (DRC)", "Costa Rica", "Croatia", "Cyprus", "Czech Republic",
  "Denmark", "Dominican Republic",
  "Ecuador", "Egypt", "El Salvador", "Equatorial Guinea", "Estonia", "Ethiopia",
  "Finland", "France",
  "Gabon", "Georgia", "Germany", "Ghana", "Greece", "Guatemala", "Guyana",
  "Honduras", "Hungary",
  "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy",
  "Jamaica", "Japan", "Jordan",
  "Kazakhstan", "Kenya", "Kuwait", "Kyrgyzstan",
  "Latvia", "Lebanon", "Libya", "Lithuania", "Luxembourg",
  "Malaysia", "Malta", "Mauritania", "Mexico", "Moldova", "Mongolia", "Montenegro", "Morocco", "Mozambique", "Myanmar",
  "Namibia", "Netherlands", "New Zealand", "Nicaragua", "Niger", "Nigeria", "North Macedonia", "Norway",
  "Oman",
  "Pakistan", "Panama", "Papua New Guinea", "Paraguay", "Peru", "Philippines", "Poland", "Portugal",
  "Qatar",
  "Romania", "Russia", "Rwanda",
  "Saudi Arabia", "Senegal", "Serbia", "Singapore", "Slovakia", "Slovenia", "South Africa", "South Korea", "Spain", "Sri Lanka", "Sudan", "Suriname", "Sweden", "Switzerland", "Syria",
  "Taiwan", "Tajikistan", "Tanzania", "Thailand", "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan",
  "Uganda", "Ukraine", "United Arab Emirates", "United Kingdom", "United States", "Uruguay", "Uzbekistan",
  "Venezuela", "Vietnam",
  "Yemen",
  "Zambia", "Zimbabwe"
];

const contactAboutOptions = [
  "General enquiry",
  "Product demo / trial",
  "Subscription / pricing",
  "Research request",
  "Data/API access",
  "Partnership / collaboration",
  "Media / speaking",
  "Technical support"
];

const contactPreferenceOptions = [
  "Email",
  "Phone call",
  "WhatsApp",
  "Video meeting"
];

const industryOptions = [
  "Oil & Gas",
  "LNG / Gas Markets",
  "Power & Renewables",
  "Mining & Critical Minerals",
  "Finance / Investment",
  "Government / Regulatory",
  "Consulting / Advisory",
  "Shipping / Maritime",
  "Manufacturing / Industrials",
  "Other"
];

const ContactPage = () => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company: '',
    country: '',
    job_title: '',
    contact_about: '',
    contact_preference: '',
    industry: '',
    description: '',
    agree_terms: false,
    subscribe_updates: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!formData.first_name) newErrors.first_name = 'First name is required';
    if (!formData.last_name) newErrors.last_name = 'Last name is required';
    if (!formData.email) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Invalid email format';
    if (!formData.phone) newErrors.phone = 'Phone number is required';
    if (!formData.company) newErrors.company = 'Company name is required';
    if (!formData.country) newErrors.country = 'Country is required';
    if (!formData.job_title) newErrors.job_title = 'Job title is required';
    if (!formData.contact_about) newErrors.contact_about = 'Please select a topic';
    if (!formData.contact_preference) newErrors.contact_preference = 'Please select preference';
    if (!formData.industry) newErrors.industry = 'Please select industry';
    if (!formData.description) newErrors.description = 'Description is required';
    if (!formData.agree_terms) newErrors.agree_terms = 'You must agree to the terms';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const result = await submitContactForm(formData);
      if (result.success) {
        setIsSubmitted(true);
        toast.success(result.message);
      }
    } catch (error) {
      toast.error('Failed to submit enquiry. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setIsSubmitted(false);
    setFormData({
      first_name: '', last_name: '', email: '', phone: '', company: '',
      country: '', job_title: '', contact_about: '', contact_preference: '',
      industry: '', description: '', agree_terms: false, subscribe_updates: false
    });
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#1F7A4F] via-[#2C6E73] to-[#284A86] text-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 whitespace-nowrap">Contact Us</h1>
            <p className="text-base lg:text-lg text-white/90 leading-relaxed">
              Connect with our team of energy intelligence experts. We're here to help you make informed decisions.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="py-16 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {isSubmitted ? (
            <div className="max-w-2xl mx-auto text-center">
              <div className="bg-white rounded-xl shadow-lg p-12">
                <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-6" />
                <h2 className="text-3xl font-bold text-slate-900 mb-4">Thank You</h2>
                <p className="text-lg text-slate-600 mb-8">
                  Your enquiry has been received. Our team will contact you within 24-48 hours.
                </p>
                <button
                  onClick={resetForm}
                  data-testid="submit-another-btn"
                  className="px-6 py-3 bg-blue-700 text-white font-semibold rounded-md hover:bg-blue-800 transition-colors"
                >
                  Submit Another Enquiry
                </button>
              </div>
            </div>
          ) : (
            <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
              {/* Left Column - 2/5 width */}
              <div className="lg:col-span-2">
                <h2 className="text-4xl font-bold text-slate-900 mb-6">Make an enquiry</h2>
                <p className="text-lg text-slate-600 leading-relaxed mb-8">
                  We approach every relationship with a unique and invigorating perspective. Share your enquiry and our team will respond promptly.
                </p>
                
                {/* Guide Points */}
                <ul className="space-y-3 mb-10">
                  <li className="flex items-start space-x-3">
                    <div className="w-1.5 h-1.5 bg-blue-700 rounded-full mt-2.5 flex-shrink-0"></div>
                    <span className="text-slate-600">Request a demo or trial access</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <div className="w-1.5 h-1.5 bg-blue-700 rounded-full mt-2.5 flex-shrink-0"></div>
                    <span className="text-slate-600">Ask about coverage, methodology, or deliverables</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <div className="w-1.5 h-1.5 bg-blue-700 rounded-full mt-2.5 flex-shrink-0"></div>
                    <span className="text-slate-600">Discuss enterprise plans, licensing, or partnerships</span>
                  </li>
                </ul>

                {/* Other Ways to Reach Us */}
                <div className="bg-white rounded-lg p-6 border border-slate-200 shadow-sm">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">Other ways to reach us</h3>
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <Mail className="w-5 h-5 text-blue-700" />
                      <a href="mailto:info@enerlligence.com" className="text-slate-600 hover:text-blue-700 transition-colors">
                        info@enerlligence.com
                      </a>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Phone className="w-5 h-5 text-blue-700" />
                      <span className="text-slate-600">Customer Support</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Clock className="w-5 h-5 text-blue-700" />
                      <span className="text-slate-600">Mon-Fri, 9:00-17:00 (WAT)</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <MessageSquare className="w-5 h-5 text-blue-700" />
                      <span className="text-slate-600">Response within 24-48 hours</span>
                    </div>
                  </div>
                </div>

                {/* Global Presence Card */}
                <div className="bg-white rounded-lg p-6 border border-slate-200 shadow-sm mt-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">Global Presence</h3>
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <MapPin className="w-5 h-5 text-blue-700 mt-0.5" />
                      <div>
                        <p className="font-medium text-slate-700">Headquarters</p>
                        <p className="text-slate-600 text-sm">Lagos, Nigeria</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <Globe className="w-5 h-5 text-blue-700 mt-0.5" />
                      <div>
                        <p className="font-medium text-slate-700">Coverage</p>
                        <p className="text-slate-600 text-sm">200+ Countries Worldwide</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - 3/5 width - Form Card */}
              <div className="lg:col-span-3">
                <div className="bg-white rounded-xl shadow-lg p-8 border border-slate-200">
                  {/* Card Header */}
                  <div className="mb-8">
                    <h3 className="text-2xl font-bold text-slate-900 mb-2">Contact us</h3>
                    <p className="text-sm text-slate-500">
                      <span className="text-red-500">*</span> Indicates required fields
                    </p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    {/* Row 1: First Name, Last Name */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          First name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="first_name"
                          value={formData.first_name}
                          onChange={handleChange}
                          data-testid="first-name-input"
                          className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${errors.first_name ? 'border-red-500' : 'border-slate-300'}`}
                          placeholder="John"
                        />
                        {errors.first_name && <p className="text-red-500 text-sm mt-1">{errors.first_name}</p>}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Last name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="last_name"
                          value={formData.last_name}
                          onChange={handleChange}
                          data-testid="last-name-input"
                          className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${errors.last_name ? 'border-red-500' : 'border-slate-300'}`}
                          placeholder="Smith"
                        />
                        {errors.last_name && <p className="text-red-500 text-sm mt-1">{errors.last_name}</p>}
                      </div>
                    </div>

                    {/* Row 2: Email, Phone */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Work Email <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          data-testid="email-input"
                          className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${errors.email ? 'border-red-500' : 'border-slate-300'}`}
                          placeholder="john.smith@company.com"
                        />
                        {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Phone number <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          data-testid="phone-input"
                          className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${errors.phone ? 'border-red-500' : 'border-slate-300'}`}
                          placeholder="+1 (555) 000-0000"
                        />
                        {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                      </div>
                    </div>

                    {/* Row 3: Company, Country */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Company name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          data-testid="company-input"
                          className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${errors.company ? 'border-red-500' : 'border-slate-300'}`}
                          placeholder="Your Organization"
                        />
                        {errors.company && <p className="text-red-500 text-sm mt-1">{errors.company}</p>}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Country <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="country"
                          value={formData.country}
                          onChange={handleChange}
                          data-testid="country-select"
                          className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${errors.country ? 'border-red-500' : 'border-slate-300'}`}
                        >
                          <option value="">Please Select</option>
                          {countries.map((country) => (
                            <option key={country} value={country}>{country}</option>
                          ))}
                        </select>
                        {errors.country && <p className="text-red-500 text-sm mt-1">{errors.country}</p>}
                      </div>
                    </div>

                    {/* Row 4: Job Title, Contact About */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Job title <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="job_title"
                          value={formData.job_title}
                          onChange={handleChange}
                          data-testid="job-title-input"
                          className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${errors.job_title ? 'border-red-500' : 'border-slate-300'}`}
                          placeholder="Your Role"
                        />
                        {errors.job_title && <p className="text-red-500 text-sm mt-1">{errors.job_title}</p>}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Contact About <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="contact_about"
                          value={formData.contact_about}
                          onChange={handleChange}
                          data-testid="contact-about-select"
                          className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${errors.contact_about ? 'border-red-500' : 'border-slate-300'}`}
                        >
                          <option value="">Please Select</option>
                          {contactAboutOptions.map((option) => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                        {errors.contact_about && <p className="text-red-500 text-sm mt-1">{errors.contact_about}</p>}
                      </div>
                    </div>

                    {/* Row 5: Contact Preference, Industry */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Contact Preference <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="contact_preference"
                          value={formData.contact_preference}
                          onChange={handleChange}
                          data-testid="contact-preference-select"
                          className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${errors.contact_preference ? 'border-red-500' : 'border-slate-300'}`}
                        >
                          <option value="">Please Select</option>
                          {contactPreferenceOptions.map((option) => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                        {errors.contact_preference && <p className="text-red-500 text-sm mt-1">{errors.contact_preference}</p>}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Industry <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="industry"
                          value={formData.industry}
                          onChange={handleChange}
                          data-testid="industry-select"
                          className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${errors.industry ? 'border-red-500' : 'border-slate-300'}`}
                        >
                          <option value="">Please Select</option>
                          {industryOptions.map((option) => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                        {errors.industry && <p className="text-red-500 text-sm mt-1">{errors.industry}</p>}
                      </div>
                    </div>

                    {/* Description */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Description <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        rows={4}
                        data-testid="description-textarea"
                        className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none ${errors.description ? 'border-red-500' : 'border-slate-300'}`}
                        placeholder="Tell us about your intelligence requirements..."
                      />
                      {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description}</p>}
                    </div>

                    {/* Consent Section */}
                    <div className="space-y-4 pt-4 border-t border-slate-200">
                      <p className="text-sm text-slate-600">
                        By submitting this form, you agree to our{' '}
                        <Link to="/terms-of-service" className="text-blue-700 hover:underline">Terms & Conditions</Link>,{' '}
                        <Link to="/privacy-policy" className="text-blue-700 hover:underline">Privacy Policy</Link>, and{' '}
                        <Link to="/cookie-policy" className="text-blue-700 hover:underline">Cookie Policy</Link>.
                      </p>
                      
                      <div className="flex items-start space-x-3">
                        <input
                          type="checkbox"
                          name="agree_terms"
                          id="agree_terms"
                          checked={formData.agree_terms}
                          onChange={handleChange}
                          data-testid="agree-terms-checkbox"
                          className="mt-1 w-4 h-4 text-blue-700 border-slate-300 rounded focus:ring-blue-500"
                        />
                        <label htmlFor="agree_terms" className="text-sm text-slate-700">
                          I agree to the Terms & Conditions and acknowledge the Privacy Policy. <span className="text-red-500">*</span>
                        </label>
                      </div>
                      {errors.agree_terms && <p className="text-red-500 text-sm">{errors.agree_terms}</p>}

                      <div className="flex items-start space-x-3">
                        <input
                          type="checkbox"
                          name="subscribe_updates"
                          id="subscribe_updates"
                          checked={formData.subscribe_updates}
                          onChange={handleChange}
                          data-testid="subscribe-updates-checkbox"
                          className="mt-1 w-4 h-4 text-blue-700 border-slate-300 rounded focus:ring-blue-500"
                        />
                        <label htmlFor="subscribe_updates" className="text-sm text-slate-700">
                          I would like to receive Enerlligence updates and insights.
                        </label>
                      </div>
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={isLoading}
                      data-testid="submit-contact-btn"
                      className="w-full py-4 bg-blue-700 text-white font-semibold rounded-md hover:bg-blue-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                    >
                      {isLoading ? (
                        <span className="flex items-center justify-center">
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Submitting...
                        </span>
                      ) : 'Submit'}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
